import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { GameCase } from "app/types";
import Constants from "common/constant";
import { useScale, usePlaySound } from "common/utils";
import { ParentCloseBtn, FadeTransition, CreatorGameCaseManager } from "common/elements";
import i18n from "features/parent/i18n";
import { ProjectEntity } from "features/creator/types";

interface ProjectPreviewModalProps {
  project: ProjectEntity;
  onClose: () => void;
}

export const ProjectPreviewModal = ({ project, onClose }: ProjectPreviewModalProps) => {
  usePlaySound();
  const { scale } = useScale();
  const GameCaseComponent = CreatorGameCaseManager[GameCase.GRAY];

  return (
    <Dialog
      fullScreen
      open={true}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el justify-start w-[600px] relative border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-view justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              {/* ゲーム機枠デザイン - UserProgressCreator.tsxと同じ構造 */}
              <div className="flex-col-center relative mb-4">
                <div
                  className="flex-col-center w-[400px] h-[330px]"
                  style={{
                    filter: "drop-shadow(0px 0px 3px rgba(63, 74, 97, 0.3))",
                  }}
                >
                  <img
                    alt="game case"
                    src={`${Constants.assetHost}/assets/images/${GameCaseComponent}`}
                    className="w-full h-full object-fill"
                  />
                  {/* アプリカード */}
                  <div className="flex-col-center !absolute top-[-10px] left-[28px] z-[-1]">
                    <img
                      alt="app card"
                      src={`${Constants.assetHost}/assets/images/app_card_user.png`}
                      className="w-[50px] h-[80px]"
                    />
                  </div>
                  {/* キャプチャ画像 */}
                  <div className="flex-col-el flex-center w-[340px] h-[270px] absolute top-[30px] left-[26px] !box-content border-solid border-gray-100 border-[4px] rounded-[32px] overflow-hidden">
                    {project.cover || project.capture ? (
                      <div className="flex-col-center w-full h-full overflow-hidden">
                        <img
                          alt="アプリのキャプチャー"
                          className="w-full h-full object-cover"
                          src={project.cover ?? project.capture}
                          onError={(e) =>
                            ((e.target as HTMLImageElement).src =
                              `${Constants.assetHost}/assets/images/default_capture.png`)
                          }
                        />
                      </div>
                    ) : (
                      <div className="w-full h-full bg-white" />
                    )}
                  </div>
                </div>
              </div>

              {/* プロジェクト情報 */}
              <div className="flex-col-el gap-3">
                <h2 className="text-[36px] text-textcolor-black">{project.name}</h2>

                <div className="flex-row-el items-center gap-6 text-[21px] text-gray2-100">
                  {project.published ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-60/20 text-green-100 rounded-full text-[19px]">
                      <span className="material-symbols-outlined text-[21px]">public</span>
                      {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_PUBLISHED")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray2-20 text-gray2-100 rounded-full text-[19px]">
                      <span className="material-symbols-outlined text-[21px]">lock</span>
                      {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_UNPUBLISHED")}
                    </span>
                  )}
                  <span className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-[24px]">visibility</span>
                    {project.viewed} {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_VIEWS")}
                  </span>
                  <span className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-[24px]">favorite</span>
                    {project.favorited} {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_LIKES")}
                  </span>
                  <span className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-[24px]">content_copy</span>
                    {project.copied} {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_COPIES")}
                  </span>
                </div>

                <div className="flex-row-el items-center gap-4 text-[19px] text-gray2-100 pt-2 border-t border-outline">
                  <span>
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_CREATED")}:{" "}
                    {dayjs(project.created_time).format("YYYY-MM-DD HH:mm")}
                  </span>
                  <span>
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_PROJECT_UPDATED")}:{" "}
                    {dayjs(project.updated_time).format("YYYY-MM-DD HH:mm")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
